import { useEffect } from "react";

import {
  LoadingSpinner,
  Input,
  Combobox,
  typographyStyles,
  Icon,
  CheckCircleFilledSVG,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import { FE_USER_ROLES_CONSTANTS } from "@/admin/constants/AdminConsoleUserRolesConstants";
import { FACULTY_ADDER_ACTIONS } from "@/admin/hooks/useFacultyAdderReducer";
import useValidateUserEmail from "@/users/useValidateUserEmail";
import { getInputErrorMessages } from "./AdminConsoleFacultyAdderUserSection.utils";
import {
  inputsGridContainerCss,
  comboboxCss,
} from "./AdminConsoleFacultyAdderUserSection.style";

const propTypes = {
  // Component form values.
  formValues: PropTypes.shape({
    firstName: PropTypes.shape({
      value: PropTypes.string,
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
    lastName: PropTypes.shape({
      value: PropTypes.string,
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
    email: PropTypes.shape({
      value: PropTypes.string,
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
    role: PropTypes.shape({
      value: PropTypes.string,
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
  }),
  // Parent reducer dispatcher.
  dispatchAction: PropTypes.func,
};

const ROLES_LIST = [
  FE_USER_ROLES_CONSTANTS.TEACHER,
  FE_USER_ROLES_CONSTANTS.SCHOOL_ADMIN,
  FE_USER_ROLES_CONSTANTS.DISTRICT_ADMIN,
];

const AdminConsoleFacultyAdderUserSection = ({
  formValues,
  dispatchAction,
}) => {
  const [firstNameError, lastNameError, emailError, roleError] =
    getInputErrorMessages(formValues);

  const { isAvailable, error, isLoading } = useValidateUserEmail(
    formValues?.email?.value,
  );

  useEffect(() => {
    dispatchAction({
      type: FACULTY_ADDER_ACTIONS.SET_USER_SECTION,
      payload: {
        ...formValues,
        email: {
          ...formValues?.email,
          isAvailable,
        },
      },
    });
  }, [isAvailable]);

  const updateTouchedControls = (e) => {
    dispatchAction({
      type: FACULTY_ADDER_ACTIONS.SET_USER_SECTION,
      payload: {
        ...formValues,
        [e.target.name]: {
          ...formValues[e.target.name],
          touched: true,
        },
      },
    });
  };

  const handleInputChange = (e) => {
    dispatchAction({
      type: FACULTY_ADDER_ACTIONS.SET_USER_SECTION,
      payload: {
        ...formValues,
        [e.target.name]: {
          ...formValues[e.target.name],
          value: e.target.value,
        },
      },
    });
  };

  return (
    <div css={inputsGridContainerCss}>
      <div>
        <Input
          name="firstName"
          label="First name"
          value={formValues?.firstName?.value}
          maxLength={50}
          touched={formValues?.firstName?.touched}
          error={firstNameError}
          onChange={handleInputChange}
          onBlur={updateTouchedControls}
        />
      </div>
      <div>
        <Input
          name="lastName"
          label="Last name"
          value={formValues?.lastName?.value}
          maxLength={50}
          touched={formValues?.lastName?.touched}
          error={lastNameError}
          onChange={handleInputChange}
          onBlur={updateTouchedControls}
        />
      </div>
      <div>
        <Input
          name="email"
          label="Email address"
          value={formValues?.email?.value}
          maxLength={100}
          touched={formValues?.email?.touched}
          error={error || emailError}
          onChange={handleInputChange}
          onBlur={updateTouchedControls}
          __cssFor={{ label: typographyStyles.heading6Obj }}
        />
        {isLoading && (
          <LoadingSpinner
            __cssFor={{
              root: {
                width: "20px",
                height: "20px",
                margin: 0,
              },
            }}
          />
        )}
        {isAvailable && (
          <Icon
            SvgComponent={CheckCircleFilledSVG}
            __cssFor={{
              root: {
                width: "20px",
                height: "20px",
              },
            }}
          />
        )}
      </div>
      <div>
        <Combobox
          label="Role"
          items={ROLES_LIST}
          selectedItem={formValues?.role?.value}
          touched={formValues?.role?.touched}
          error={roleError}
          onSelectedItemChange={(e) => {
            dispatchAction({
              type: FACULTY_ADDER_ACTIONS.SET_USER_SECTION,
              payload: {
                ...formValues,
                role: {
                  ...formValues.role,
                  value: e.inputValue,
                  touched: true,
                },
              },
            });
          }}
          __cssFor={{
            root: comboboxCss,
          }}
        />
      </div>
    </div>
  );
};

AdminConsoleFacultyAdderUserSection.propTypes = propTypes;

export default AdminConsoleFacultyAdderUserSection;
